import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchViolations } from '../../store/slices/adminSlice';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Divider, Stack, Alert, Tooltip
} from '@mui/material';
import {
  Warning, Visibility, Close, Person, Business, Schedule, Block,
  ReportProblem
} from '@mui/icons-material';
import api from '../../services/api';

const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : 'N/A');

const AdminViolations = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { violations, loading } = useSelector((state) => state.admin);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');

  useEffect(() => { dispatch(fetchViolations()); }, [dispatch]);

  const openDetail = async (room) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/violations/${room._id}`);
      if (!data?.data) {
        throw new Error('Empty response from server. Restart the backend so the new endpoint is loaded.');
      }
      setDetail(data.data);
    } catch (err) {
      console.error('[Violations] detail load failed', err);
      const status = err.response?.status;
      const apiMsg = err.response?.data?.error;
      const fallback = status === 404
        ? 'This audit endpoint is not available — restart the backend (npm run dev / pm2 restart) to enable /admin/violations/:roomId.'
        : err.message;
      setDetailError(apiMsg || fallback);
    }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setDetailError('');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <Warning color="error" />
        <Typography variant="h5" fontWeight={700}>{t('violations')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Contact information sharing attempts detected by AI monitoring in chat conversations.
        Open a row to review the original (pre-redaction) message text, time and sender before it is purged.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#ffebee' }}>
              <TableCell>Chat Room</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell align="center">Attempts</TableCell>
              <TableCell>Risk Level</TableCell>
              <TableCell>Last Violation</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {violations?.map((v) => {
              const attempts = v.aiMonitoring?.contactInfoAttempts || 0;
              const lastAt = v.aiMonitoring?.lastViolation;
              const high = !!v.aiMonitoring?.isHighRisk;
              const customerName = v.customer
                ? `${v.customer.firstName || ''} ${v.customer.lastName || ''}`.trim()
                  || v.customer.customerId
                  || v.customer.email
                : '—';
              const supplierName = v.supplier?.companyName || '—';
              return (
                <TableRow key={v._id} hover>
                  <TableCell>{v.roomId || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.customer?.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{supplierName}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.supplier?.internalCode}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={attempts} size="small"
                      color={attempts > 3 ? 'error' : attempts > 0 ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={high ? 'HIGH RISK' : 'Low'}
                      color={high ? 'error' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>{formatDateTime(lastAt)}</TableCell>
                  <TableCell>
                    <Chip label={v.status || 'active'} size="small"
                      color={v.status === 'resolved' ? 'success'
                        : v.status === 'flagged' ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Open violation details">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Visibility />}
                        onClick={() => openDetail(v)}
                      >
                        Open
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!violations || violations.length === 0) && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No violations detected
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Violation Detail Dialog ─── */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ffebee' }}>
          <ReportProblem color="error" />
          Violation Audit — {detail?.roomId || ''}
          <IconButton sx={{ ml: 'auto' }} onClick={closeDetail}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}

          {detail && !detailLoading && (
            <>
              <Stack spacing={1} mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Person fontSize="small" color="primary" />
                  <Typography variant="body2">
                    <strong>Customer:</strong>{' '}
                    {[detail.customer?.firstName, detail.customer?.lastName].filter(Boolean).join(' ') || '—'}
                    {detail.customer?.email ? ` <${detail.customer.email}>` : ''}
                    {detail.customer?.customerId ? ` · ${detail.customer.customerId}` : ''}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Business fontSize="small" color="primary" />
                  <Typography variant="body2">
                    <strong>Supplier:</strong>{' '}
                    {detail.supplier?.companyName || '—'}
                    {detail.supplier?.internalCode ? ` (${detail.supplier.internalCode})` : ''}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Schedule fontSize="small" color="action" />
                  <Typography variant="body2">
                    Total messages: <strong>{detail.totalMessages}</strong>
                    &nbsp;·&nbsp; Flagged: <strong>{detail.flaggedMessages.length}</strong>
                    &nbsp;·&nbsp; Last violation: <strong>{formatDateTime(detail.aiMonitoring?.lastViolation)}</strong>
                  </Typography>
                  {detail.aiMonitoring?.isHighRisk && (
                    <Chip size="small" color="error" icon={<Block />} label="HIGH RISK" />
                  )}
                </Box>
                {detail.flagReason && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Reason: {detail.flagReason}
                  </Alert>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                Flagged messages ({detail.flaggedMessages.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Original (pre-redaction) text is shown when available. Review here while
                still on file — records may be purged after the platform's retention window.
              </Typography>

              {detail.flaggedMessages.length === 0 ? (
                <Alert severity="info">
                  No raw flagged content is preserved for this room. The violation counter was
                  incremented but the original message text is no longer available.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {detail.flaggedMessages.map((m) => {
                    const who = m.sender?.name || m.senderRole || 'Unknown';
                    const role = m.sender?.role || m.senderRole;
                    const isWarning = m.type === 'ai_warning';
                    return (
                      <Paper key={m._id} variant="outlined"
                        sx={{
                          p: 2,
                          borderLeft: '4px solid',
                          borderLeftColor: isWarning ? 'info.main' : 'error.main',
                          bgcolor: isWarning ? '#e3f2fd' : '#fff8f8'
                        }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start"
                          mb={1} flexWrap="wrap" gap={1}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} component="div">
                              {who}
                              <Chip size="small"
                                label={role || 'unknown'}
                                sx={{ ml: 1, textTransform: 'capitalize' }}
                                color={role === 'customer' ? 'primary'
                                  : role === 'supplier_employee' ? 'secondary' : 'default'}
                              />
                              {isWarning && <Chip size="small" sx={{ ml: 1 }} color="info" label="AI WARNING" />}
                              {m.wasFiltered && <Chip size="small" sx={{ ml: 1 }} color="error" label="REDACTED" />}
                            </Typography>
                            {m.sender?.email && (
                              <Typography variant="caption" color="text.secondary">
                                {m.sender.email}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(m.createdAt)}
                          </Typography>
                        </Box>

                        {m.originalContent && m.originalContent !== m.content ? (
                          <>
                            <Typography variant="caption" color="error.main" fontWeight={700}>
                              Original (pre-filter):
                            </Typography>
                            <Box sx={{
                              p: 1, mb: 1, bgcolor: 'rgba(244,67,54,0.08)',
                              border: '1px dashed', borderColor: 'error.light',
                              borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                            }}>
                              {m.originalContent}
                            </Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Delivered (after filter):
                            </Typography>
                            <Box sx={{
                              p: 1, bgcolor: 'background.default',
                              border: '1px solid', borderColor: 'divider',
                              borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                            }}>
                              {m.content}
                            </Box>
                          </>
                        ) : (
                          <Box sx={{
                            p: 1, bgcolor: 'background.default',
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                          }}>
                            {m.content}
                          </Box>
                        )}

                        {m.filterReason && (
                          <Typography variant="caption" color="error.main" display="block" mt={1}>
                            Filter reason: {m.filterReason}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminViolations;
